/**
 * Foundry API Client
 * Frontend API functions for interacting with Foundry backend
 */

import type {
  BusinessPlan,
  BusinessPlanWithSections,
  PlanSection,
  CreatePlanRequest,
  UpdatePlanRequest,
  GenerateCanvasRequest,
  GenerateCanvasResponse,
  ExpandSectionRequest,
  ExpandSectionResponse,
  SanityCheckResult,
  SectionKey,
  RawIdea,
  UserContext,
  OrganizedIdeas,
  GuidedQuestion,
  OrganizeIdeasResponse,
  GenerateQuestionsResponse,
  GenerateRefinementQuestionsResponse,
  RefinementQuestion,
  RefinementAnswer,
  SavedRefinementAnswers,
  FollowUpQuestion,
  UserProfile,
  UserProfileSettings,
  UserProfilePatterns,
  UserLearning,
} from './types';

const BASE_URL = '/api/secure/foundry';

// Cache CSRF token
let csrfToken: string | null = null;

/**
 * Fetch CSRF token from the server
 */
async function fetchCsrfToken(): Promise<string | null> {
  try {
    const response = await fetch('/api/secure/csrf-token', {
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('[Foundry] Failed to fetch CSRF token');
      return null;
    }

    const data = await response.json();
    csrfToken = data.csrfToken || null;
    return csrfToken;
  } catch (error) {
    console.error('[Foundry] Failed to fetch CSRF token:', error);
    return null;
  }
}

/**
 * Generic API call wrapper with error handling and CSRF token support
 */
async function apiCall<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Include CSRF token for state-changing requests
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    // Fetch token if we don't have one
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));

    // If CSRF token is invalid, clear it and retry once
    if (response.status === 403 && error.error === 'CSRF validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
        const retryResponse = await fetch(url, {
          ...options,
          credentials: 'include',
          headers,
        });
        if (retryResponse.ok) {
          return retryResponse.json();
        }
      }
    }

    throw new Error(error.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Plan CRUD Operations
// ============================================================================

/**
 * List all plans for the current user
 */
export async function listPlans(): Promise<BusinessPlan[]> {
  const data = await apiCall<{ plans: BusinessPlan[] }>(`${BASE_URL}/plans`);
  return data.plans;
}

/**
 * Get a single plan with all sections
 */
export async function getPlan(planId: string): Promise<BusinessPlanWithSections> {
  return apiCall<BusinessPlanWithSections>(`${BASE_URL}/plans/${planId}`);
}

/**
 * Create a new plan
 */
export async function createPlan(data: CreatePlanRequest): Promise<BusinessPlan> {
  const result = await apiCall<{ plan: BusinessPlan }>(`${BASE_URL}/plans`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.plan;
}

/**
 * Update an existing plan
 */
export async function updatePlan(
  planId: string,
  data: UpdatePlanRequest
): Promise<BusinessPlan> {
  const result = await apiCall<{ plan: BusinessPlan }>(
    `${BASE_URL}/plans/${planId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return result.plan;
}

/**
 * Delete a plan
 */
export async function deletePlan(planId: string): Promise<void> {
  await apiCall<{ success: boolean }>(`${BASE_URL}/plans/${planId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// AI Operations
// ============================================================================

/**
 * Generate a Lean Canvas from a business idea
 */
export async function generateCanvas(
  planId: string,
  data: GenerateCanvasRequest
): Promise<GenerateCanvasResponse> {
  return apiCall<GenerateCanvasResponse>(
    `${BASE_URL}/plans/${planId}/ai/generate-canvas`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Expand a section using AI
 */
export async function expandSection(
  planId: string,
  data: ExpandSectionRequest
): Promise<{ section: PlanSection; suggestions: string[] }> {
  return apiCall<{ section: PlanSection; suggestions: string[] }>(
    `${BASE_URL}/plans/${planId}/ai/expand-section`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Run sanity checks on the plan
 */
export async function runSanityCheck(
  planId: string
): Promise<SanityCheckResult> {
  const data = await apiCall<{ result: SanityCheckResult }>(
    `${BASE_URL}/plans/${planId}/ai/sanity-check`,
    {
      method: 'POST',
    }
  );
  return data.result;
}

/**
 * Generate AI-powered refinement questions for a section
 * Returns cached questions if content hasn't changed
 */
export async function generateRefinementQuestions(
  planId: string,
  sectionKey: SectionKey
): Promise<GenerateRefinementQuestionsResponse & { cached: boolean }> {
  return apiCall<GenerateRefinementQuestionsResponse & { cached: boolean }>(
    `${BASE_URL}/plans/${planId}/ai/generate-refinement-questions`,
    {
      method: 'POST',
      body: JSON.stringify({ section_key: sectionKey }),
    }
  );
}

/**
 * Generate follow-up questions based on an answer
 */
export async function generateFollowUpQuestions(
  planId: string,
  sectionKey: SectionKey,
  questionText: string,
  answerText: string,
  answerId: string
): Promise<{ follow_up_questions: FollowUpQuestion[] }> {
  return apiCall<{ follow_up_questions: FollowUpQuestion[] }>(
    `${BASE_URL}/plans/${planId}/ai/generate-follow-up-questions`,
    {
      method: 'POST',
      body: JSON.stringify({
        section_key: sectionKey,
        question_text: questionText,
        answer_text: answerText,
        answer_id: answerId,
      }),
    }
  );
}

// ============================================================================
// Idea Capture Operations (Brain Dump Workflow)
// ============================================================================

/**
 * Organize raw ideas into themes and identify gaps
 */
export async function organizeIdeas(
  planId: string,
  rawIdeas: RawIdea[],
  userContext?: Partial<UserContext>
): Promise<OrganizeIdeasResponse> {
  return apiCall<OrganizeIdeasResponse>(
    `${BASE_URL}/plans/${planId}/ai/organize-ideas`,
    {
      method: 'POST',
      body: JSON.stringify({ raw_ideas: rawIdeas, user_context: userContext }),
    }
  );
}

/**
 * Generate targeted questions based on organized themes
 */
export async function generateQuestions(
  planId: string,
  organized?: OrganizedIdeas,
  existingAnswers?: Record<string, string>
): Promise<GenerateQuestionsResponse> {
  return apiCall<GenerateQuestionsResponse>(
    `${BASE_URL}/plans/${planId}/ai/generate-questions`,
    {
      method: 'POST',
      body: JSON.stringify({ organized, existing_answers: existingAnswers }),
    }
  );
}

/**
 * Generate canvas from organized ideas and answered questions
 */
export async function generateCanvasFromIdeas(
  planId: string,
  answers: Record<string, string>
): Promise<GenerateCanvasResponse> {
  return apiCall<GenerateCanvasResponse>(
    `${BASE_URL}/plans/${planId}/ai/generate-canvas-from-ideas`,
    {
      method: 'POST',
      body: JSON.stringify({ answers }),
    }
  );
}

// ============================================================================
// Export Operations
// ============================================================================

/**
 * Export plan as Markdown (returns download URL)
 */
export function getMarkdownExportUrl(planId: string): string {
  return `${BASE_URL}/plans/${planId}/export/markdown`;
}

/**
 * Download plan as Markdown
 */
export async function downloadMarkdown(planId: string): Promise<void> {
  const url = getMarkdownExportUrl(planId);
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    throw new Error('Failed to export plan');
  }

  // Get filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="(.+)"/);
  const filename = filenameMatch?.[1] || 'business-plan.md';

  // Create blob and download
  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(downloadUrl);
}

// ============================================================================
// User Profile Operations
// ============================================================================

/**
 * Get or create the user's profile
 */
export async function getProfile(): Promise<UserProfile> {
  const data = await apiCall<{ profile: UserProfile }>(`${BASE_URL}/profile`);
  return data.profile;
}

/**
 * Update user profile patterns (AI-inferred)
 */
export async function updateProfilePatterns(
  patterns: Partial<UserProfilePatterns>
): Promise<UserProfile> {
  const data = await apiCall<{ profile: UserProfile }>(
    `${BASE_URL}/profile`,
    {
      method: 'PUT',
      body: JSON.stringify({ patterns }),
    }
  );
  return data.profile;
}

/**
 * Update user-controlled profile settings
 */
export async function updateProfileSettings(
  settings: Partial<UserProfileSettings>
): Promise<UserProfile> {
  const data = await apiCall<{ profile: UserProfile }>(
    `${BASE_URL}/profile/settings`,
    {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }
  );
  return data.profile;
}

/**
 * Extract learnings from a plan and update profile
 */
export async function extractLearnings(
  planId: string
): Promise<{
  learning: UserLearning;
  updated_patterns: UserProfilePatterns;
  profile: UserProfile;
}> {
  return apiCall<{
    learning: UserLearning;
    updated_patterns: UserProfilePatterns;
    profile: UserProfile;
  }>(
    `${BASE_URL}/profile/extract-learnings`,
    {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    }
  );
}

// ============================================================================
// Refinement Answers
// ============================================================================

/**
 * Get saved refinement answers for a section
 */
export async function getRefinementAnswers(
  planId: string,
  sectionKey: SectionKey
): Promise<SavedRefinementAnswers> {
  const data = await apiCall<{ refinement_answers: SavedRefinementAnswers }>(
    `${BASE_URL}/plans/${planId}/sections/${sectionKey}/refinement-answers`
  );
  return data.refinement_answers;
}

/**
 * Save or update refinement answers for a section
 */
export async function saveRefinementAnswers(
  planId: string,
  sectionKey: SectionKey,
  answers: RefinementAnswer[]
): Promise<SavedRefinementAnswers> {
  const data = await apiCall<{ refinement_answers: SavedRefinementAnswers }>(
    `${BASE_URL}/plans/${planId}/sections/${sectionKey}/refinement-answers`,
    {
      method: 'PUT',
      body: JSON.stringify({ answers }),
    }
  );
  return data.refinement_answers;
}

/**
 * Delete specific refinement answers (or all if no IDs provided)
 */
export async function deleteRefinementAnswers(
  planId: string,
  sectionKey: SectionKey,
  answerIds?: string[]
): Promise<SavedRefinementAnswers> {
  const data = await apiCall<{ refinement_answers: SavedRefinementAnswers }>(
    `${BASE_URL}/plans/${planId}/sections/${sectionKey}/refinement-answers`,
    {
      method: 'DELETE',
      body: answerIds ? JSON.stringify({ answer_ids: answerIds }) : undefined,
    }
  );
  return data.refinement_answers;
}
